import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { ISavedCardChargeQuote } from "shared";

import { Modal } from "../../../common/Atoms/Modal";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Button } from "../../../common/Atoms/Button";
import { Notification } from "../../../common/Atoms/Notification";
import { RadioStack } from "../../../common/Atoms/RadioStack";
import { ErrorPayload } from "../../../common/lib/axiosBaseQuery";
import { formatAmount } from "../../../common/lib/helpers";
import { usePaySavedCardMutation } from "../../services/api/invoiceApi/invoice";

interface PayWithSavedCardModalProps {
  open: boolean;
  onClose: () => void;
  quote: ISavedCardChargeQuote;
  invoiceNumber: string;
  /** Offered when the charge fails so the customer can pay through the portal instead */
  onSendReminder: () => void;
}

export function PayWithSavedCardModal({ open, onClose, quote, invoiceNumber, onSendReminder }: PayWithSavedCardModalProps) {
  const { organisationId, invoiceId } = useParams();
  const [paySavedCard, { isLoading, error, reset }] = usePaySavedCardMutation();
  const [selected, setSelected] = useState<string>(``);

  const defaultId = (quote.paymentMethods.find(m => m.isDefault) || quote.paymentMethods[0])?.id || ``;

  useEffect(() => {
    if (open) {
      setSelected(defaultId);
    }
  }, [open, defaultId]);

  // Not in the effect above: `reset` changes identity when the mutation fires, which would wipe its loading state
  function close() {
    reset();
    onClose();
  }

  const total = formatAmount(quote.amountCents + quote.surchargeCents, quote.currency);
  const errorMessage = (error as ErrorPayload | undefined)?.data?.message;
  // Only a 402 is Stripe definitively refusing the card; anything else may still have charged it
  const cardDeclined = (error as ErrorPayload | undefined)?.status === 402;

  async function onPay() {
    const result = await paySavedCard({ organisationId, invoiceId, paymentMethodId: selected });

    if (`error` in result) {
      return;
    }

    toast.success(result.data.synced ?
      `Charged ${total} to the saved card` :
      `Charged ${total} to the saved card. The payment is still syncing to your accounting system.`);

    onClose();
  }

  return (
    <Modal
      open={ open }
      setOpen={ close }
      title={ `` }
    >
      <div className={ `flex flex-col items-center pb-4` }>
        <Heading>
          { `Pay with saved card` }
        </Heading>
        <Paragraph variant={ `primary` }>
          { quote.instalmentNumber ?
            `Charge installment #${quote.instalmentNumber} of invoice ${invoiceNumber} to a card the customer saved in the portal.` :
            `Charge invoice ${invoiceNumber} to a card the customer saved in the portal.` }
        </Paragraph>
      </div>

      <div className={ `space-y-3` }>
        <RadioStack
          label={ `Card` }
          options={ quote.paymentMethods.map(m => ({
            value: m.id,
            title: `${m.label}${m.isDefault ? ` (default)` : ``}`,
            description: m.expMonth ? `Expires ${String(m.expMonth).padStart(2, `0`)}/${m.expYear}` : undefined,
          })) }
          selected={ selected }
          onChange={ e => setSelected(e.value) }
        />

        <div className={ `text-sm text-gray-700` }>
          <div className={ `flex justify-between` }>
            <span>{ quote.instalmentNumber ? `Installment due` : `Amount due` }</span>
            <span>{ formatAmount(quote.amountCents, quote.currency) }</span>
          </div>
          { quote.surchargeCents > 0 && (
            <div className={ `flex justify-between` }>
              <span>{ `Card processing fee` }</span>
              <span>{ formatAmount(quote.surchargeCents, quote.currency) }</span>
            </div>
          ) }
          <div className={ `flex justify-between font-semibold` }>
            <span>{ `Total to charge` }</span>
            <span>{ total }</span>
          </div>
        </div>

        { errorMessage && (
          <Notification
            type={ `error` }
            title={ cardDeclined ? `The card was not charged` : `The payment could not be completed` }
            buttons={ cardDeclined && (
              <Button
                color={ `gray` }
                onClick={ () => {
                  close();
                  onSendReminder();
                } }
              >
                { `Email a payment link instead` }
              </Button>
            ) }
          >
            { errorMessage }
          </Notification>
        ) }

        <Button
          className={ `w-full justify-center` }
          size={ `lg` }
          disabled={ !selected || isLoading }
          loading={ isLoading }
          loadingText={ `Charging ${total}...` }
          onClick={ onPay }
        >
          { `Charge ${total}` }
        </Button>
      </div>
    </Modal>
  );
}
